import { createORPCClient } from '@orpc/client'
import { OpenAPILink } from '@orpc/openapi-client/fetch'
import type { ContractRouterClient } from '@orpc/contract'
import { contract } from '@behuman/core-contract'

export type OrpcClient = ContractRouterClient<typeof contract>

export function createOrpcClient(opts: {
  baseUrl: string
  getToken: () => string | undefined
  /**
   * 401-retry-once hook (PD-1512): called when a request comes back 401; resolves to a
   * fresh access token (the request is retried once with it) or undefined (the 401
   * stands). Never invoked for /v2/auth/* — those 401 legitimately (bad credentials,
   * expired sessions) and retrying a login would double-fire it.
   */
  recoverAuth?: () => Promise<string | undefined>
}): OrpcClient {
  const link = new OpenAPILink(contract, {
    url: opts.baseUrl,
    headers: () => {
      const token = opts.getToken()
      return token ? { Authorization: `Bearer ${token}` } : {}
    },
    // The /v2/auth endpoints authenticate via an httpOnly cookie (PD-1512); the cookie is
    // path-scoped to /v2/auth server-side, so including credentials globally attaches it
    // only there. Requires the middleware's credentialed CORS (origin reflect).
    fetch: async (request, init) => {
      const withCredentials = { ...init, credentials: 'include' as const }
      // Clone before the first send — a retry needs the unconsumed body.
      const response = await globalThis.fetch(request.clone(), withCredentials)

      if (
        response.status !== 401 ||
        !opts.recoverAuth ||
        new URL(request.url).pathname.startsWith('/v2/auth')
      ) {
        return response
      }

      const token = await opts.recoverAuth()
      if (!token) {
        return response
      }

      const headers = new Headers(request.headers)
      headers.set('Authorization', `Bearer ${token}`)
      return globalThis.fetch(new Request(request, { headers }), withCredentials)
    },
  })
  return createORPCClient<OrpcClient>(link)
}
